import useBottomScrollAnchor from 'hooks/useBottomScrollAnchor';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AssistantMessageStatusType } from 'utils/chat/chatConstants';
import { getMessageText } from 'utils/chat/chatHelpers';

const THREAD_BOTTOM_STICK_THRESHOLD_PX = 80;
const THREAD_BOTTOM_SCROLL_MAX_FRAMES = 30;
const OLDER_MESSAGES_LOAD_SCROLL_TOP_THRESHOLD_PX = 48;
const WHEEL_UP_DELTA_Y_THRESHOLD = 0;
const THREAD_CONTENT_KEY_SEPARATOR = ':';
const REQUESTED_INTERACTION_KEY_SEPARATOR = '|';
const ThreadContentRunState = {
    IDLE: 'idle',
};
const WheelDirections = {
    UP: 'up',
};
const SCROLL_CONTROL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'End', 'Home', 'PageDown', 'PageUp', ' ']);

const getRequestedInteractionScrollKey = (message) => {
    const custom = message?.metadata?.custom || {};
    const requestedInteraction = custom.requested_interaction;
    const interactionResponse = custom.interaction_response;

    return [
        requestedInteraction?.id || '',
        requestedInteraction?.input_type || requestedInteraction?.action_type || '',
        requestedInteraction?.prompt_text || '',
        (requestedInteraction?.options || requestedInteraction?.actions || []).length,
        interactionResponse?.requested_interaction_id || '',
    ].join(THREAD_CONTENT_KEY_SEPARATOR);
};

const getThreadContentKey = ({ isRunning, manualAssistantDraft, messages }) => {
    const lastMessage = messages[messages.length - 1];
    const requestedInteractionScrollKey = messages
        .map(getRequestedInteractionScrollKey)
        .join(REQUESTED_INTERACTION_KEY_SEPARATOR);

    return [
        messages.length,
        lastMessage?.id || '',
        getMessageText(lastMessage?.content || []).length,
        manualAssistantDraft.length,
        isRunning ? AssistantMessageStatusType.RUNNING : ThreadContentRunState.IDLE,
        requestedInteractionScrollKey,
    ].join(THREAD_CONTENT_KEY_SEPARATOR);
};

const getWheelDirection = (event) => {
    if (event.deltaY < WHEEL_UP_DELTA_Y_THRESHOLD) return WheelDirections.UP;
    return null;
};

const useChatThreadScroll = ({
    hasOlderMessages,
    isLoadingOlderMessages,
    isRetrying,
    isRunning,
    manualAssistantDraft,
    messages,
    onLoadOlderMessages,
    shouldScrollThreadToBottom,
    showSuggestions,
    suggestionsLength,
    threadViewKey,
}) => {
    const threadViewportRef = useRef(null);
    const hasRequestedOlderMessagesRef = useRef(false);
    const isThreadBottomingPendingRef = useRef(false);
    const threadBottomingFrameRef = useRef(null);
    const olderMessagesScrollRestoreRef = useRef(null);
    const [showTopScrollGradient, setShowTopScrollGradient] = useState(false);
    const threadContentKey = getThreadContentKey({ isRunning, manualAssistantDraft, messages });

    const { pauseBottomScrollAnchor, resumeBottomScrollAnchor, updateBottomScrollAnchor } = useBottomScrollAnchor({
        containerRef: threadViewportRef,
        contentKey: threadContentKey,
        isActive: true,
        isPaused: isLoadingOlderMessages,
        thresholdPx: THREAD_BOTTOM_STICK_THRESHOLD_PX,
    });

    useLayoutEffect(() => {
        if (!olderMessagesScrollRestoreRef.current || !threadViewportRef.current || isLoadingOlderMessages) return;

        const { previousScrollHeight, previousScrollTop } = olderMessagesScrollRestoreRef.current;
        const nextScrollHeight = threadViewportRef.current.scrollHeight;

        threadViewportRef.current.scrollTop = previousScrollTop + (nextScrollHeight - previousScrollHeight);
        olderMessagesScrollRestoreRef.current = null;
    }, [isLoadingOlderMessages, messages.length]);

    useLayoutEffect(() => {
        if (shouldScrollThreadToBottom) {
            isThreadBottomingPendingRef.current = true;
        }

        if (!isThreadBottomingPendingRef.current || !threadViewportRef.current || isLoadingOlderMessages) {
            return undefined;
        }

        if (threadBottomingFrameRef.current) {
            cancelAnimationFrame(threadBottomingFrameRef.current);
        }

        let frameCount = 0;
        resumeBottomScrollAnchor();
        threadViewportRef.current.scrollTop = threadViewportRef.current.scrollHeight;

        const scrollToBottomUntilSettled = () => {
            if (!isThreadBottomingPendingRef.current || !threadViewportRef.current || isLoadingOlderMessages) {
                threadBottomingFrameRef.current = null;
                return;
            }

            frameCount += 1;
            threadViewportRef.current.scrollTop = threadViewportRef.current.scrollHeight;

            if (frameCount >= THREAD_BOTTOM_SCROLL_MAX_FRAMES) {
                isThreadBottomingPendingRef.current = false;
                threadBottomingFrameRef.current = null;
                return;
            }

            threadBottomingFrameRef.current = requestAnimationFrame(scrollToBottomUntilSettled);
        };

        threadBottomingFrameRef.current = requestAnimationFrame(scrollToBottomUntilSettled);

        return () => {
            if (threadBottomingFrameRef.current) {
                cancelAnimationFrame(threadBottomingFrameRef.current);
                threadBottomingFrameRef.current = null;
            }
        };
    }, [
        isLoadingOlderMessages,
        resumeBottomScrollAnchor,
        shouldScrollThreadToBottom,
        threadContentKey,
        threadViewKey,
    ]);

    useEffect(() => {
        const element = threadViewportRef.current;
        if (!element) return;

        setShowTopScrollGradient(element.scrollTop > 0);
    }, [messages.length, showSuggestions, suggestionsLength]);

    const pauseAutoScrollForUserInput = () => {
        isThreadBottomingPendingRef.current = false;
        if (threadBottomingFrameRef.current) {
            cancelAnimationFrame(threadBottomingFrameRef.current);
            threadBottomingFrameRef.current = null;
        }
        pauseBottomScrollAnchor();
    };

    const onThreadScroll = async (event) => {
        updateBottomScrollAnchor(event.currentTarget);
        setShowTopScrollGradient(event.currentTarget.scrollTop > 0);

        if (event.currentTarget.scrollTop > OLDER_MESSAGES_LOAD_SCROLL_TOP_THRESHOLD_PX) {
            hasRequestedOlderMessagesRef.current = false;
            return;
        }

        if (!hasOlderMessages || isLoadingOlderMessages || !onLoadOlderMessages) return;
        if (isRunning || isRetrying) return;
        if (hasRequestedOlderMessagesRef.current) return;

        hasRequestedOlderMessagesRef.current = true;
        olderMessagesScrollRestoreRef.current = {
            previousScrollHeight: event.currentTarget.scrollHeight,
            previousScrollTop: event.currentTarget.scrollTop,
        };
        await onLoadOlderMessages();
    };

    const onThreadWheel = (event) => {
        if (getWheelDirection(event) === WheelDirections.UP) {
            pauseAutoScrollForUserInput();
        }
    };

    const onThreadKeyDown = (event) => {
        if (!SCROLL_CONTROL_KEYS.has(event.key)) return;
        pauseAutoScrollForUserInput();
    };

    return {
        onThreadKeyDown,
        onThreadScroll,
        onThreadTouchMove: pauseAutoScrollForUserInput,
        onThreadWheel,
        showTopScrollGradient,
        threadViewportRef,
    };
};

export default useChatThreadScroll;
